'use client';

import useTenant from '@/state/tenant';
import { Separator } from '@mokoboko/components';

import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import {
  getTenantAddress,
  getTenantDirectionsUrl,
  getTenantMapQuery,
} from '@/helpers/tenant';
import { PREVIEW_ADDRESS, useIsPreview } from '@/contexts/preview';

export const OurLocationSection = () => {
  const { data: tenant } = useTenant();
  const isPreview = useIsPreview();

  const mapsApiKey = tenant?.location_config?.maps_api_key;
  const query = getTenantMapQuery(tenant);

  // A studio that has not entered a Maps key would read an empty tab as the
  // toggle being broken, so the preview stands a picture of a map in.
  const showPlaceholderMap = isPreview && !mapsApiKey;

  if (!showPlaceholderMap && (!mapsApiKey || !query)) return null;

  const address = getTenantAddress(
    tenant,
    showPlaceholderMap ? PREVIEW_ADDRESS : null
  );
  const directionsUrl = getTenantDirectionsUrl(tenant);
  const embedSrc =
    mapsApiKey && query
      ? `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsApiKey)}&q=${encodeURIComponent(query)}`
      : null;

  return (
    <div className="flex flex-col gap-6">
      <Separator />
      <section className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="heading-section">Our location</h2>
          {directionsUrl ? (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-focus-visible w-fit rounded text-sm text-gray-700 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-900 hover:decoration-gray-900"
              onClick={() =>
                track({ event: AnalyticsEvent.DIRECTIONS_CLICKED })
              }
            >
              {address ?? 'Get directions'}
            </a>
          ) : address ? (
            <p className="text-sm text-gray-700">{address}</p>
          ) : null}
        </div>

        <div className="h-[160px] w-full overflow-hidden rounded-xl">
          {embedSrc ? (
            <iframe
              title="Studio location"
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              src={embedSrc}
            />
          ) : (
            <img
              src="/images/preview/map.webp"
              alt=""
              className="h-full w-full object-cover"
            />
          )}
        </div>
      </section>
    </div>
  );
};
